import { deepEqual } from "fast-equals";
import { Model, modelAction, prop } from "mobx-keystone";
import { globalNS } from "./ns.global";

const MIN_LOAD_TIME = 1 * 500; // 500ms

interface LoadingState {
  loading?: boolean;
  meta?: React.ReactNode;
  timeout?: number;
  canCancel?: boolean;
}

interface IncomingLoading extends LoadingState {
  loading: true;
}
interface IncomingMeta extends LoadingState {
  loading?: true;
  meta: React.ReactNode;
}
interface IncomingUnloading extends Omit<LoadingState, "meta" | "timeout"> {
  loading: false;
}

type Uncancellable<T extends { canCancel?: boolean }> = Omit<T, "canCancel">;
export type LoaderState = IncomingLoading | IncomingMeta | IncomingUnloading;
export type IncomingLoaderState =
  | Uncancellable<IncomingLoading>
  | Uncancellable<IncomingMeta>
  | Uncancellable<IncomingUnloading>;

@globalNS.etc("loader")
export default class LoaderModel extends Model({
  loading: prop<boolean>(false),
  meta: prop<React.ReactNode | undefined>(() => null),
  canCancel: prop<boolean | undefined>(() => false).withSetter(),
}) {
  inProcess = false;

  loadQueue: IncomingLoaderState[] = [];

  resetTimer: NodeJS.Timeout | null = null;
  loadTimer: NodeJS.Timeout | null = null;

  @modelAction
  setGlobalLoading(state: IncomingLoaderState) {
    // console.info("Incoming loader state", state);
    if (!this.loadQueue.find((queueState) => deepEqual(queueState, state))) {
      this.loadQueue.push(state);
    }

    if (!this.loadTimer) {
      this.activateLoadingQueue(!this.inProcess);
    }
  }

  @modelAction
  updateCurrentLoadState(state: Partial<LoaderState>) {
    const { loading } = state;

    if (typeof loading === "boolean") this.loading = loading;

    if (loading) {
      const { meta, canCancel } = state;
      if (meta) this.meta = meta;
      if (canCancel) this.canCancel = canCancel;
    } else {
      this.meta = null;
      this.canCancel = false;
    }
  }

  @modelAction
  activateLoadingState(state: LoaderState) {
    this.inProcess = true;
    this.updateCurrentLoadState({ ...state, canCancel: false });
    // changing the meta does not change the cancellation window timer
    if (state.loading) {
      this.resetTimer = setTimeout(
        () => {
          this.setCanCancel(true);
        },
        state.timeout ?? 1 * 1000 * 60,
      );
    }
  }

  @modelAction
  deactivateLoadingState() {
    this.inProcess = false;
    this.updateCurrentLoadState({
      loading: false,
      canCancel: false,
    });

    // reset timer states
    if (this.loadTimer) {
      clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }
    if (this.resetTimer) {
      clearTimeout(this.resetTimer ?? undefined);
      this.resetTimer = null;
    }
  }

  dequeue = () => {
    let next = this.loadQueue.shift();
    if (next) {
      // if next state is false but any other future states are true we can skip setting to false
      // otherwise. we're done
      if (next.loading === false) {
        const nextLoadStateIndex = this.loadQueue.findIndex(
          (state) => state.loading === true,
        );
        if (nextLoadStateIndex >= 0) {
          this.loadQueue.splice(0, nextLoadStateIndex);
          next = this.loadQueue.shift();
          if (next) {
            this.activateLoadingState(next);
          }
        } else {
          this.deactivateLoadingState();
        }
      } else {
        this.activateLoadingState(next);
      }
    }
  };

  activateLoadingQueue(immediate?: boolean) {
    this.loadTimer = setInterval(() => this.dequeue(), MIN_LOAD_TIME);
    if (immediate) this.dequeue();
  }

  protected onAttachedToRootStore() {
    this.loadQueue = [];
  }
}
